import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  InputAdornment,
  Slider,
  Divider,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FreelancerCard, { Freelancer } from '../components/FreelancerCard';
import { searchFreelancers, getPopularSkills, getAllProfiles, testSupabaseConnection, FreelancerProfile } from '../lib/api/freelancers';
import { checkItemOwnership, getPromotionStatus } from '../lib/api/promotionBids';
import { trackSearch } from '../lib/analytics';
import PromotionModal from '../components/PromotionModal';

// Helper function to convert FreelancerProfile to Freelancer
const convertToFreelancer = (profile: FreelancerProfile): Freelancer => {
  const converted = {
    id: profile.id,
    name: profile.full_name || 'Без име',
    title: profile.company || `${profile.role === 'AGENCY' ? 'Агенция' : 'Фриленсър'}`,
    avatar: profile.avatar_url || '/images/default-avatar.png',
    rating: profile.rating_avg || 0,
    reviewCount: profile.rating_cnt || 0,
    location: profile.location || 'Не е посочена',
    skills: profile.skills || [],
    hourlyRate: profile.hourly_rate || 0,
    completedProjects: profile.completed_jobs || 0,
    responseTime: '1-2 часа', // Default response time
    description: profile.bio || 'Няма описание',
    isOnline: Math.random() > 0.5, // Random online status for now
    isVerified: profile.verified || false,
    // Required fields for Freelancer interface
    experience: '5+ години опит',
    education: [],
    certifications: [],
    workHistory: [],
    portfolio: [],
    languages: [],
    availability: 'На разположение',
    timezone: 'UTC+2',
    joinedDate: profile.created_at || new Date().toISOString(),
  };
  
  console.log('Converted freelancer:', converted);
  return converted;
};

const skillCategories = [
  'Web Development',
  'Mobile Development',
  'Design',
  'Marketing',
  'Writing',
  'Data Science',
  'DevOps',
  'Consulting',
];

const FindTalentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<number[]>([0, 1000]);
  const [ratingFilter, setRatingFilter] = useState(0);
  const [sortBy, setSortBy] = useState('rating');
  const [showFilters, setShowFilters] = useState(false);
  
  // New state for real data
  const [freelancers, setFreelancers] = useState<Freelancer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [popularSkills, setPopularSkills] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [promotedFreelancers, setPromotedFreelancers] = useState<Set<string>>(new Set());
  const [freelancerOwnership, setFreelancerOwnership] = useState<Set<string>>(new Set());
  const [promotionModal, setPromotionModal] = useState<{
    open: boolean;
    itemId: string;
    itemTitle: string;
  }>({ open: false, itemId: '', itemTitle: '' });

  // Load freelancers on component mount only
  useEffect(() => {
    loadFreelancers();
  }, []);

  // Load popular skills on component mount
  useEffect(() => {
    loadPopularSkills();
    // Test functions to debug
    testAllProfiles();
    testConnection();
  }, []);

  const testAllProfiles = async () => {
    try {
      const { data, error } = await getAllProfiles();
      console.log('Test - All profiles:', { data, error });
      if (data) {
        console.log('Total profiles found:', data.length);
        console.log('Profiles with FREELANCER role:', data.filter(p => p.role === 'FREELANCER').length);
        console.log('Profiles with AGENCY role:', data.filter(p => p.role === 'AGENCY').length);
        console.log('Sample profile:', data[0]);
      }
    } catch (err) {
      console.error('Test error:', err);
    }
  };

  const testConnection = async () => {
    try {
      const { success, error } = await testSupabaseConnection();
      console.log('Supabase connection test:', { success, error });
    } catch (err) {
      console.error('Connection test error:', err);
    }
  };

  const loadFreelancers = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Loading ALL freelancers and agencies...');
      
      // Track search if there's a search term
      if (searchTerm.trim()) {
        trackSearch(searchTerm.trim(), 'freelancers');
      }
      
      // Get ALL freelancers and agencies without any filters
      const { data, error, count } = await searchFreelancers();

      if (error) {
        console.error('Search error:', error);
        setError(error);
        return;
      }

      console.log('Search result:', { data, count });

      // If no data from database, just show empty list
      if (!data || data.length === 0) {
        console.log('No data from database');
        setFreelancers([]);
        setTotalCount(0);
        return;
      }

      // Filter out client profiles - only show freelancers and agencies
      const freelancerAndAgencyProfiles = data.filter(
        profile => profile.role === 'FREELANCER' || profile.role === 'AGENCY'
      );
      
      const convertedFreelancers = freelancerAndAgencyProfiles.map(convertToFreelancer);
      
      console.log(`Converted ${convertedFreelancers.length} freelancers/agencies`);
      console.log('First converted freelancer:', convertedFreelancers[0]);
      
      // Check ownership and promotion status for each freelancer
      const ownershipAndPromotionChecks = await Promise.all(
        convertedFreelancers.map(async (freelancer) => {
          const [isOwner, promotionStatus] = await Promise.all([
            checkItemOwnership(freelancer.id, 'FREELANCER_PROFILE'),
            getPromotionStatus(freelancer.id, 'FREELANCER_PROFILE').catch(() => ({ isPromoted: false }))
          ]);
          console.log('Ownership and promotion check for freelancer:', freelancer.id, freelancer.name, 'isOwner:', isOwner, 'isPromoted:', promotionStatus.isPromoted);
          return { 
            freelancerId: freelancer.id, 
            isOwner, 
            isPromoted: promotionStatus.isPromoted 
          };
        })
      );
      
      const ownedFreelancerIds = new Set(
        ownershipAndPromotionChecks
          .filter(check => check.isOwner)
          .map(check => check.freelancerId)
      );
      setFreelancerOwnership(ownedFreelancerIds);
      
      const promotedFreelancerIds = new Set(
        ownershipAndPromotionChecks
          .filter(check => check.isPromoted)
          .map(check => check.freelancerId)
      );
      setPromotedFreelancers(promotedFreelancerIds);
      
      console.log('Ownership results:', {
        total: convertedFreelancers.length,
        owned: ownedFreelancerIds.size,
        ownedIds: Array.from(ownedFreelancerIds)
      });
      
      setFreelancers(convertedFreelancers);
      setTotalCount(count);
      
      console.log('Freelancers state set:', convertedFreelancers.length);
    } catch (err) {
      setError('Грешка при зареждане на данните');
      console.error('Error loading freelancers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPopularSkills = async () => {
    try {
      const { data, error } = await getPopularSkills();
      if (!error && data) {
        setPopularSkills(data);
      }
    } catch (err) {
      console.error('Error loading popular skills:', err);
    }
  };

  const filteredFreelancers = useMemo(() => {
    console.log('Filtering freelancers:', {
      total: freelancers.length,
      selectedCategory,
      priceRange,
      sortBy
    });
    
    let filtered = [...freelancers]; // Create a copy

    // Client-side filtering for category (since we don't have category in DB yet)
    if (selectedCategory) {
      filtered = filtered.filter(freelancer =>
        freelancer.skills.some(skill => 
          skill.toLowerCase().includes(selectedCategory.toLowerCase())
        )
      );
    }

    // Client-side price range filter (additional to server-side)
    filtered = filtered.filter(freelancer =>
      freelancer.hourlyRate >= priceRange[0] && freelancer.hourlyRate <= priceRange[1]
    );

    // Always sort to show promoted freelancers first, then apply other sorting
    // First, separate promoted and non-promoted freelancers
    const promotedGroup = filtered.filter(freelancer => promotedFreelancers.has(freelancer.id));
    const nonPromotedGroup = filtered.filter(freelancer => !promotedFreelancers.has(freelancer.id));
    
    // Apply additional sorting within each group
    const sortGroup = (group: Freelancer[]) => {
      group.sort((a, b) => {
        switch (sortBy) {
          case 'rating':
            return b.rating - a.rating;
          case 'price-low':
            return a.hourlyRate - b.hourlyRate;
          case 'price-high':
            return b.hourlyRate - a.hourlyRate;
          case 'projects':
            return b.completedProjects - a.completedProjects;
          default:
            return 0;
        }
      });
    };
    
    sortGroup(promotedGroup);
    sortGroup(nonPromotedGroup);
    
    // Combine: promoted first, then non-promoted
    filtered = [...promotedGroup, ...nonPromotedGroup];

    console.log('Filtered result:', filtered.length);
    return filtered;
  }, [freelancers, selectedCategory, priceRange, sortBy]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedSkills([]);
    setPriceRange([0, 1000]);
    setRatingFilter(0);
    setSortBy('rating');
  };


  const handleViewProfile = (freelancer: Freelancer) => {
    navigate(`/freelancer/${freelancer.id}`);
  };

  const handlePromotionClick = (freelancerId: string, freelancerName: string) => {
    setPromotionModal({
      open: true,
      itemId: freelancerId,
      itemTitle: freelancerName
    });
  };

  const handlePromotionSuccess = () => {
    // Reload freelancers to get updated data
    loadFreelancers();
  };

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <PageSEO pageKey="findTalent" />
      <Header />
      <Box sx={{ py: { xs: 2, sm: 4 } }}>
        <Container maxWidth="xl" sx={{ width: { xs: '95%', sm: '90%', md: '80%' } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h2"
            sx={{ 
              color: '#ffffff', 
              fontWeight: 800, 
              mb: 2,
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Намери талант
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#a0a0a0', 
            mb: 4, 
            maxWidth: 600, 
            mx: 'auto',
            fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
            px: { xs: 2, sm: 0 }
          }}>
            Открий перфектния професионалист за твоя проект и започни работа днес
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            mb: { xs: 3, sm: 4 },
            borderRadius: { xs: 2, sm: 3 },
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
              {/* Search */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder={t('findTalent.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#a0a0a0' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& fieldset': {
                        borderColor: '#2d2d44',
                      },
                      '&:hover fieldset': {
                        borderColor: '#28A0F0',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#28A0F0',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#a0a0a0',
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                    },
                  }}
                />
              </Grid>

              {/* Sort */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#a0a0a0' }}>{t('findTalent.sortBy')}</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label={t('findTalent.sortBy')}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2d2d44',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28A0F0',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28A0F0',
                      },
                    }}
                  >
                    <MenuItem value="rating">{t('findTalent.sortOptions.rating')}</MenuItem>
                    <MenuItem value="price-low">{t('findTalent.sortOptions.priceLow')}</MenuItem>
                    <MenuItem value="price-high">{t('findTalent.sortOptions.priceHigh')}</MenuItem>
                    <MenuItem value="projects">{t('findTalent.sortOptions.projects')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Filter Toggle */}
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    borderColor: '#2d2d44',
                    color: '#ffffff',
                    '&:hover': {
                      borderColor: '#28A0F0',
                      backgroundColor: 'rgba(40, 160, 240, 0.1)',
                    },
                  }}
                >
                  {t('findTalent.filters')}
                </Button>
              </Grid>
            </Grid>

            {/* Advanced Filters */}
            {showFilters && (
              <>
                <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                <Grid container spacing={3}>
                  {/* Category Filter */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: '#a0a0a0' }}>{t('findTalent.category')}</InputLabel>
                      <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        label={t('findTalent.category')}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2d2d44',
                          },
                        }}
                      >
                        <MenuItem value="">{t('findTalent.allCategories')}</MenuItem>
                        {skillCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Skills Filter */}
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 1 }}>
                        {t('findTalent.skills')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {popularSkills.slice(0, 10).map((skill) => (
                          <Chip
                            key={skill}
                            label={skill}
                            clickable
                            color={selectedSkills.includes(skill) ? 'primary' : 'default'}
                            onClick={() => {
                              setSelectedSkills(prev =>
                                prev.includes(skill)
                                  ? prev.filter(s => s !== skill)
                                  : [...prev, skill]
                              );
                            }}
                            sx={{
                              backgroundColor: selectedSkills.includes(skill)
                                ? 'rgba(40, 160, 240, 0.2)'
                                : 'rgba(255, 255, 255, 0.1)',
                              color: selectedSkills.includes(skill) ? '#28A0F0' : '#a0a0a0',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Price Range */}
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 2 }}>
                        {t('findTalent.hourlyRate')}: ${priceRange[0]} - ${priceRange[1]}
                      </Typography>
                      <Slider
                        value={priceRange}
                        onChange={(_, newValue) => setPriceRange(newValue as number[])}
                        valueLabelDisplay="auto"
                        min={0}
                        max={1000}
                        sx={{
                          color: '#28A0F0',
                          '& .MuiSlider-thumb': {
                            backgroundColor: '#28A0F0',
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Rating Filter */}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 2 }}>
                        {t('findTalent.minRating')}: {ratingFilter}+
                      </Typography>
                      <Slider
                        value={ratingFilter}
                        onChange={(_, newValue) => setRatingFilter(newValue as number)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={5}
                        step={0.5}
                        sx={{
                          color: '#28A0F0',
                          '& .MuiSlider-thumb': {
                            backgroundColor: '#28A0F0',
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Clear Filters */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                      <Button
                        variant="outlined"
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                        sx={{
                          borderColor: '#ef4444',
                          color: '#ef4444',
                          '&:hover': {
                            borderColor: '#dc2626',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          },
                        }}
                      >
                        {t('findTalent.clearFilters')}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Results Count */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography variant="body1" sx={{ 
            color: '#a0a0a0',
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}>
            {t('findTalent.resultsCount', { count: totalCount })}
          </Typography>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#28A0F0' }} />
          </Box>
        )}

        {/* Freelancers List */}
        {!loading && (
          <Stack spacing={{ xs: 2, sm: 3 }}>
            {filteredFreelancers.map((freelancer) => {
              console.log('Rendering freelancer:', freelancer);
              return (
                <FreelancerCard
                  key={freelancer.id}
                  freelancer={freelancer}
                  onViewProfile={handleViewProfile}
                  isPromoted={promotedFreelancers.has(freelancer.id)}
                  onTogglePromotion={(freelancerId) => handlePromotionClick(freelancerId, freelancer.name || 'Фрийленсър')}
                  canPromote={freelancerOwnership.has(freelancer.id)}
                />
              );
            })}
          </Stack>
        )}


        {/* No Results */}
        {filteredFreelancers.length === 0 && (
          <Paper
            sx={{
              p: { xs: 4, sm: 6 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: { xs: 2, sm: 3 },
            }}
          >
            <Typography variant="h6" sx={{ 
              color: '#a0a0a0', 
              mb: 2,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}>
              {t('findTalent.noResults')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClearFilters}
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                color: '#ffffff',
                px: { xs: 3, sm: 4 },
                py: { xs: 1.2, sm: 1.5 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                borderRadius: { xs: 2, sm: 3 },
              }}
            >
              {t('findTalent.clearFiltersAndSearch')}
            </Button>
          </Paper>
        )}
      </Container>
      </Box>
      <Footer />

      {/* Promotion Modal */}
      <PromotionModal
        open={promotionModal.open}
        onClose={() => setPromotionModal({ open: false, itemId: '', itemTitle: '' })}
        itemId={promotionModal.itemId}
        itemType="FREELANCER_PROFILE"
        itemTitle={promotionModal.itemTitle}
        onPromotionSuccess={handlePromotionSuccess}
      />
    </Box>
  );
};

export default FindTalentPage;
